<template>
  <div class="form__row--x">
    <div class="form__row">
      <div class="form__row__item">
        <label>Manner of Delivery</label>
        <select v-model="deliveryManner" :style="highlightIfError('deliveryManner')" :disabled="isClose || isTolac === null">
          <option :value="null" disabled v-if="isTolac === null">Please answer if patient underwent TOLAC</option>
          <option
              v-for="mode in filteredDeliveryManners"
              :value="mode.id"
              :key="mode.id"
              :disabled="mode.disabled"
          >
            {{ mode.name }}
          </option>
        </select>
      </div>
      <div class="smd-spacer"></div>
    </div>

    <div class="form__row--x" v-if="isCS">
      <label>Primary/Repeat</label>
      <smd-segment type="pill"
                   v-model="primaryRepeat"
                   :items="$options.primaryOrRepeat()"
                   :disabled="isClose"/>
    </div>

    <div class="form__row--x" v-if="isCS">
      <label>Incision</label>
      <smd-segment type="pill"
                   v-model="incision"
                   :items="$options.incisions()"
                   :disabled="isClose"/>
    </div>

    <div class="form__row--x" v-if="isCS">
      <label>Bilateral Salpingectomy</label>
      <smd-segment type="pill"
                   v-model="CSBilateralSalpingectomy"
                   :items="$options.presenceChoices"
                   :disabled="isClose"/>
    </div>

    <div class="form__row--x" v-if="isCS">
      <label>BTL</label>
      <smd-segment type="pill"
                   v-model="CSBTL"
                   :items="$options.presenceChoices"
                   :disabled="isClose"/>
    </div>

    <div class="form__row--x" v-show="willShowIndication">
      <div class="form__row--x" v-if="!isClose">
        <label>Primary Indication (if with multiple indications, indicate the Primary Indication)</label>
        <smd-select v-model="deliveryIndicationsModel"
                    value-key="id"
                    :group-label="null"
                    :group-values="null"
                    :hide-tags="true"
                    :hide-selected="true"
                    :options="deliveryIndicationsLookup"
                    :style="highlightIfError('deliveryIndication')"/>
      </div>

      <template v-if="hasIndications">
        <div class="data-entry" >
          <label>CS Indication</label>
        </div>

        <div class="data-entry" v-for="(value, key) in deliveryIndications" :key="key">
          <p><strong>{{ decodeIndication(key)}}</strong></p>
          <p></p>
          <smd-btn icon="smd-icon-action-delete"
                   @click="removeIndication(key)"
                   v-if="!isClose"/>
        </div>
      </template>
    </div>

    <!-- Duplicated for the 2ndary manner of delivery fields -->
    <template v-if="willRequireSecondaryManner">
      <div class="form__row">
        <div class="form__row__item">
          <label>Manner of Delivery of the 2nd Twin</label>
          <select v-model="deliveryManner2" :style="highlightIfError('deliveryManner2')" :disabled="isClose">
            <option v-for="mode in filteredDeliveryManners"
                    :value="mode.id"
                    :key="mode.id">
              {{ mode.name }}
            </option>
          </select>
        </div>
        <div class="smd-spacer"></div>
      </div>

      <div class="form__row--x" v-if="isCS2">
        <label>Primary/Repeat for the 2nd Twin</label>
        <smd-segment type="pill"
                     v-model="primaryRepeat2"
                     :items="$options.primaryOrRepeat()"
                     :disabled="isClose"/>
      </div>

      <div class="form__row--x" v-if="isCS2">
        <label>Incision for the 2nd Twin</label>
        <smd-segment type="pill"
                     v-model="incision2"
                     :items="$options.incisions()"
                     :disabled="isClose"/>
      </div>

      <div class="form__row--x" v-if="isCS2">
        <label>Bilateral Salpingectomy for the 2nd Twin</label>
        <smd-segment type="pill"
                     v-model="CSBilateralSalpingectomy2"
                     :items="$options.presenceChoices"
                     :disabled="isClose"/>
      </div>

      <div class="form__row--x" v-if="isCS2">
        <label>BTL for the 2nd Twin</label>
        <smd-segment type="pill"
                     v-model="CSBTL2"
                     :items="$options.presenceChoices"
                     :disabled="isClose"/>
      </div>

      <div class="form__row--x" v-show="willShowIndication2">
        <div class="form__row--x" v-if="!isClose">
          <label>Primary Indication (if with multiple indications, indicate the Primary Indication) for the 2nd Twin</label>
          <smd-select v-model="deliveryIndicationsModel2"
                      value-key="id"
                      :group-label="null"
                      :group-values="null"
                      :hide-tags="true"
                      :hide-selected="true"
                      :options="deliveryIndicationsLookup2"
                      :style="highlightIfError('deliveryIndication')"/>
        </div>

        <template v-if="hasIndications2">
          <div class="data-entry" >
            <label>CS Indication for the 2nd Twin</label>
          </div>

          <div class="data-entry" v-for="(value, key) in deliveryIndications2" :key="key">
            <p><strong>{{ decodeIndication2(key)}}</strong></p>
            <p></p>
            <smd-btn icon="smd-icon-action-delete"
                     @click="removeIndication2(key)"
                     v-if="!isClose"/>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
  import withErrorValidation from '../../../mixins/with-error-validation-mixin';

  import {mapGetters, mapMutations, mapActions} from 'vuex'
  import {createHelpers} from 'vuex-map-fields'
  import {
    fn_getNameWithMap,
    injectDisabledProperty
  }
    from "../../../utilities/utilityOperators";

  import {
    GETTER_CASE_FORM_STATE_FIELD,
    SETTER_CASE_FORM_STATE_FIELD,
    GETTER_PREGNANCY_INDICATION,
    ACTION_UPDATE_PREGNANCY_INDICATION,
    MUTATE_REMOVE_PREGNANCY_INDICATION,
    GETTER_PREGNANCY_INDICATION2,
    ACTION_UPDATE_PREGNANCY_INDICATION2,
    MUTATE_REMOVE_PREGNANCY_INDICATION2,
  } from '../../../store/forms/case-form-state'

  import {
    getMapFn,
    REF_MATERNAL_FETAL_FORM_DELIVERY,
    REF_MATERNAL_FETAL_FORM_INDICATIONS_CS,
    REF_MATERNAL_FETAL_FORM_INDICATION_FORCEPS,
    REF_MATERNAL_FETAL_FORM_INDICATION_VACUUM,
    REF_MATERNAL_FETAL_FORM_INDICATIONS_CS_OBJ,
    REF_MATERNAL_FETAL_FORM_INDICATION_FORCEPS_OBJ,
    REF_MATERNAL_FETAL_FORM_INDICATION_VACUUM_OBJ,
    REF_MATERNAL_FETAL_FORM_PRIMARY_REPEAT,
    REF_MATERNAL_FETAL_FORM_INCISION
  } from '../../../store/modules/referencedata2'

  const {mapFields} = createHelpers({
    getterType: GETTER_CASE_FORM_STATE_FIELD,
    mutationType: SETTER_CASE_FORM_STATE_FIELD
  });

  export default {
    props: {isTolac: Boolean},
    mixins: [withErrorValidation],
    computed: {
      ...mapGetters({
        deliveryIndications: GETTER_PREGNANCY_INDICATION,
        deliveryIndications2: GETTER_PREGNANCY_INDICATION2,

      }),
      ...mapFields([
        'isPregnancy',
        'isDelivered',

        'pregnancyType',
        'deliveryManner',
        'primaryRepeat',

        // Added tagging of procedure
        'CSBilateralSalpingectomy',
        'CSBTL',
        'incision',
        'deliveryIndication',

        'deliveryManner2',
        'primaryRepeat2',

        // Added tagging of procedure
        'CSBilateralSalpingectomy2',
        'CSBTL2',
        'incision2',
        'deliveryIndication2',

        'isClose'
      ]),
      isCS() {
        let list = [1];
        return !!list.includes(this.deliveryManner)
      },
      isForceps() {
        let list = [5];
        return !!list.includes(this.deliveryManner)
      },
      isVacuum() {
        let list = [6];
        return !!list.includes(this.deliveryManner)
      },
      isCS2() {
        let list = [1];
        return !!list.includes(this.deliveryManner2)
      },
      isForceps2() {
        let list = [5];
        return !!list.includes(this.deliveryManner2)
      },
      isVacuum2() {
        let list = [6];
        return !!list.includes(this.deliveryManner2)
      },
      willRequireSecondaryManner() {
        return this.pregnancyType === 2 && (this.deliveryManner !== 1 && this.deliveryManner);
      },
      willShowNumberOfInfants() {
        return this.pregnancyType !== 1
      },
      willShowIndication() {
        return this.isCS || this.isForceps || this.isVacuum
      },
      willShowIndication2() {
        return this.isCS2 || this.isForceps2 || this.isVacuum2
      },
      deliveryIndicationsLookup() {
        let mapFnName = null;

        if (this.isCS)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATIONS_CS;
        else if (this.isForceps)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATION_FORCEPS;
        else if (this.isVacuum)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATION_VACUUM;

        return mapFnName ? injectDisabledProperty(getMapFn(mapFnName)()) : [];
      },
      deliveryIndicationsLookupObj() {
        let mapFnName = null;

        //TODO maybe a singular object for indication types regardless of  deliveryManner
        if (this.isCS)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATIONS_CS_OBJ;
        else if (this.isForceps)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATION_FORCEPS_OBJ;
        else if (this.isVacuum)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATION_VACUUM_OBJ;

        return mapFnName ? getMapFn(mapFnName)() : {};
      },
      hasIndications() {
        return !!Object.keys(this.deliveryIndications || {}).length;
      },
      deliveryIndicationsModel: {
        get() {
          const map = this.deliveryIndicationsLookupObj;
          if(this.hasIndications && Object.keys(map).length > 0) {
            const keys = Object.keys(this.deliveryIndications);

            return keys.map((v) => ({
              id: map[v].id,
              name: fn_getNameWithMap(map)(v)
            }));
          }

          return [];
        },
        set(v) {
          let value = {};
          if(v instanceof Array && v.length) {
            value = v.reduce((acc, o) => {
              acc[o.id] = {id: o.id};
              return acc;
            }, {})
          }
          this.doUpdateMultiSelectModel(value);
        }
      },
      // 2ndary fields
      deliveryIndicationsLookup2() {
        let mapFnName = null;

        if (this.isCS2)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATIONS_CS;
        else if (this.isForceps2)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATION_FORCEPS;
        else if (this.isVacuum2)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATION_VACUUM;

        return mapFnName ? injectDisabledProperty(getMapFn(mapFnName)()) : [];
      },
      deliveryIndicationsLookupObj2() {
        let mapFnName = null;

        //TODO maybe a singular object for indication types regardless of  deliveryManner
        if (this.isCS2)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATIONS_CS_OBJ;
        else if (this.isForceps2)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATION_FORCEPS_OBJ;
        else if (this.isVacuum2)
          mapFnName = REF_MATERNAL_FETAL_FORM_INDICATION_VACUUM_OBJ;

        return mapFnName ? getMapFn(mapFnName)() : {};
      },
      hasIndications2() {
        return !!Object.keys(this.deliveryIndications2 || {}).length;
      },
      deliveryIndicationsModel2: {
        get() {
          const map = this.deliveryIndicationsLookupObj2;
          if(this.hasIndications2 && Object.keys(map).length > 0) {
            const keys = Object.keys(this.deliveryIndications2);
            return keys.map((v) => ({
              id: map[v].id,
              name: fn_getNameWithMap(map)(v)
            }));
          }

          return [];
        },
        set(v) {
          let value = {};
          if(v instanceof Array && v.length) {
            value = v.reduce((acc, o) => {
              acc[o.id] = {id: o.id};
              return acc;
            }, {})
          }
          this.doUpdateMultiSelectModel2(value);
        }
      },
      filteredDeliveryManners() {
        const allManners = this.$options.deliveryManners();

        if (this.isTolac === true) {
          return allManners.filter(mode => [1, 10, 11, 12].includes(mode.id));
        } else if (this.isTolac === false) {
          return allManners;
        }
        return [];
      }
    },
    methods: {
      ...mapActions({
        doUpdateMultiSelectModel: ACTION_UPDATE_PREGNANCY_INDICATION,
        doUpdateMultiSelectModel2: ACTION_UPDATE_PREGNANCY_INDICATION2,
      }),
      ...mapMutations({
        removeIndication: MUTATE_REMOVE_PREGNANCY_INDICATION,
        removeIndication2: MUTATE_REMOVE_PREGNANCY_INDICATION2,
      }),
      decodeIndication(id) {
        const o = this.deliveryIndicationsLookupObj[id];
        return o ? o.name : "";
      },
      decodeIndication2(id) {
        const o = this.deliveryIndicationsLookupObj2[id];
        return o ? o.name : "";
      }
    },
    deliveryManners: getMapFn(REF_MATERNAL_FETAL_FORM_DELIVERY),
    primaryOrRepeat: getMapFn(REF_MATERNAL_FETAL_FORM_PRIMARY_REPEAT),
    incisions: getMapFn(REF_MATERNAL_FETAL_FORM_INCISION),
    presenceChoices: [
      {id: 1, name: 'WITH', val: true},
      {id: 2, name: 'WITHOUT', val: false}
    ],
    booleanChoices: [
      {id: 1, name: 'YES', val: true},
      {id: 2, name: 'NO', val: false}
    ],
    validationSection: 'pregnancy',
    watch :{
      isTolac(newVal) {
        if (newVal === null) {
          this.deliveryManner = null;
          this.deliveryManner2 = null;
        }
      },
      deliveryManner(newVal, oldVal) {
        // Always reset the indications list once the mode of delivery changes
        if (newVal !== oldVal) {
          this.deliveryIndicationsModel = [];
        }
        // Make sure to reset the 2ndary manner of delivery if the first one was reset
        if (newVal !== 1) {
          this.deliveryIndicationsModel2 = []
          this.deliveryManner2 = null;
          this.primaryRepeat2 = null;
          this.incision2 = null;
          this.CSBilateralSalpingectomy2 = null;
          this.CSBTL2 = null;
        }
      },
      deliveryManner2(newVal, oldVal) {
        // Always reset the indications list once the mode of delivery changes
        if (newVal !== oldVal)
          this.deliveryIndicationsModel2 = [];
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "../../../scaffolding/scss/pogs-util";

  .max-AOG{
    input{
      max-width: 115px;
    }
  }

  option[disabled] {
    color: #999;
    background-color: #f5f5f5;
    font-style: italic;
  }
</style>
